<template>
  <label :class="wrapperClasses">
    <input
      type="checkbox"
      :checked="modelValue"
      :disabled="disabled"
      class="switch__input"
      @change="onChange"
    />
    <span class="switch__track">
      <span class="switch__thumb"></span>
    </span>
    <span v-if="label || $slots.default" class="switch__label">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<script>
export default {
  name: 'BaseSwitch',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'primary',
      validator: (v) => ['primary', 'success', 'danger', 'warning', 'info'].includes(v)
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v)
    }
  },
  emits: ['update:modelValue', 'change'],
  computed: {
    wrapperClasses() {
      return [
        'switch-wrapper',
        `switch-wrapper--${this.size}`,
        `switch-wrapper--${this.color}`,
        {
          'switch-wrapper--checked': this.modelValue,
          'switch-wrapper--disabled': this.disabled
        }
      ]
    }
  },
  methods: {
    onChange(e) {
      this.$emit('update:modelValue', e.target.checked)
      this.$emit('change', e.target.checked)
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

// Size configurations
$switch-sizes: (
  sm: (track-width: 32px, track-height: 18px, thumb-size: 14px, thumb-offset: 2px),
  md: (track-width: 40px, track-height: 22px, thumb-size: 18px, thumb-offset: 2px),
  lg: (track-width: 48px, track-height: 26px, thumb-size: 22px, thumb-offset: 2px)
);

// Color configurations
$switch-colors: (
  primary: $color-primary,
  success: $color-success,
  danger: $color-danger,
  warning: $color-warning,
  info: $color-info
);

.switch-wrapper {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: $spacing-2;

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.switch__input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.switch__track {
  position: relative;
  display: inline-flex;
  align-items: center;
  background-color: $color-gray-300;
  border-radius: $radius-full;
  transition: background-color $transition-normal $transition-timing;
  flex-shrink: 0;
}

.switch__thumb {
  position: absolute;
  background-color: $color-bg-primary;
  border-radius: $radius-full;
  box-shadow: $shadow-sm;
  transition: transform $transition-normal $transition-timing;
}

.switch__label {
  font-size: $font-size-sm;
  color: $color-text-primary;
  line-height: $line-height-tight;
}

// Generate size variants
@each $size, $config in $switch-sizes {
  .switch-wrapper--#{$size} {
    .switch__track {
      width: map-get($config, track-width);
      height: map-get($config, track-height);
    }

    .switch__thumb {
      width: map-get($config, thumb-size);
      height: map-get($config, thumb-size);
      left: map-get($config, thumb-offset);
    }

    &.switch-wrapper--checked .switch__thumb {
      transform: translateX(#{map-get($config, track-width) - map-get($config, thumb-size) - map-get($config, thumb-offset) * 2});
    }
  }
}

// Generate color variants
@each $color, $value in $switch-colors {
  .switch-wrapper--#{$color}.switch-wrapper--checked {
    .switch__track {
      background-color: $value;
    }
  }
}

// Focus states
.switch__input:focus-visible + .switch__track {
  box-shadow: 0 0 0 3px $input-focus-ring-color;
}

// Hover state (not disabled)
.switch-wrapper:not(.switch-wrapper--disabled):hover {
  .switch__track {
    background-color: $color-gray-400;
  }

  @each $color, $value in $switch-colors {
    &.switch-wrapper--#{$color}.switch-wrapper--checked .switch__track {
      background-color: darken($value, 5%);
    }
  }
}
</style>
